// TermsOfService.jsx
// Drop this in your React project (e.g., src/pages/TermsOfService.jsx)
// Then add a route like: <Route path="/terms" element={<TermsOfService />} />

import React from "react";
import Footer from "../components/Footer";
import PageWrapper from "../components/PageWrapper";
import "./Terms.css";

/**
 * Simple, production-ready Terms of Service page for a small studio site.
 * Tailwind classes are used for styling; safe to use without Tailwind as well.
 * Customize companyName, domain, and contactEmail below.
 */

const companyName = "MindBlows";
const domain = "https://mindblows.com";
const contactEmail = "hello@mindblows.com"; // <-- change if needed
const effectiveDate = "August 23, 2025"; // <-- update when you publish

export default function TermsOfService() {
  const sections = [
    {
      id: "intro",
      title: "Welcome",
      content: (
        <>
          <p className="mb-4">
            Welcome to <strong>{companyName}</strong> ("we," "our," "us"). By
            accessing or using our website ({domain}) and any of our apps or
            services (together, the "Services"), you agree to these Terms of
            Service (the "Terms"). If you do not agree, you must not use the
            Services.
          </p>
        </>
      ),
    },
    {
      id: "use",
      title: "Use of Services",
      bullets: [
        "Our Services are provided for personal, non‑commercial use unless we agree otherwise in writing.",
        "You must be at least 13 years old (or the age of digital consent in your country) to use the Services.",
        "Do not misuse, interfere with, or attempt to access the Services using any method other than our provided interfaces.",
      ],
    },
    {
      id: "ip",
      title: "Intellectual Property",
      bullets: [
        `All content on the website and within our apps, including text, graphics, logos, audio, video, and software, is owned by or licensed to ${companyName}.`,
        "You may not copy, distribute, modify, reverse engineer, or create derivative works without our prior written permission.",
      ],
    },
    {
      id: "purchases",
      title: "Purchases & Payments",
      bullets: [
        "Some apps may offer in‑app purchases or subscriptions.",
        "All payments are processed by platform providers (e.g., Apple App Store, Google Play) and subject to their terms and policies.",
        "Payments are non‑refundable except as required by law or by the applicable platform policy.",
      ],
    },
    {
      id: "user-content",
      title: "User Content",
      bullets: [
        "If you submit content to us (e.g., feedback), you grant us a worldwide, non‑exclusive, royalty‑free license to use, reproduce, and share that content.",
        "You are responsible for the content you submit and must not provide anything unlawful, offensive, or infringing.",
      ],
    },
    {
      id: "disclaimers",
      title: "Disclaimers",
      bullets: [
        'The Services are provided "as is" and "as available" without warranties of any kind, express or implied.',
        "We do not guarantee uninterrupted, error‑free, or secure operation of the Services.",
        "We are not responsible for third‑party services, content, or links.",
      ],
    },
    {
      id: "liability",
      title: "Limitation of Liability",
      content: (
        <p>
          To the fullest extent permitted by law, {companyName} will not be
          liable for any indirect, incidental, special, consequential,
          exemplary, or punitive damages arising from or related to your use of
          the Services. Our total liability for any claim will not exceed the
          amount you paid (if any) for using the Services during the twelve (12)
          months before the event giving rise to the liability.
        </p>
      ),
    },
    {
      id: "termination",
      title: "Termination",
      content: (
        <p>
          We may suspend or terminate your access to the Services at any time if
          you violate these Terms or if we reasonably believe it is necessary to
          protect the Services, users, or third parties.
        </p>
      ),
    },
    {
      id: "changes",
      title: "Changes to These Terms",
      content: (
        <p>
          We may update these Terms from time to time. The updated version will
          be posted on this page with a new effective date. Changes become
          effective when posted unless we state otherwise.
        </p>
      ),
    },
    {
      id: "contact",
      title: "Contact",
      content: (
        <p>
          Questions? Reach us at{" "}
          <a className="underline" href={`mailto:${contactEmail}`}>
            {contactEmail}
          </a>
          .
        </p>
      ),
    },
  ];

  return (
    <PageWrapper>
      <div
        className="privacy-container"
        style={{
          padding: "80px 30px 30px 30px",
          maxWidth: "900px",
          margin: "auto",
          whiteSpace: "pre-wrap",
        }}
      >
        <main className="min-h-screen bg-gradient-to-b from-white to-gray-50 text-gray-900">
          <header className="bg-gradient-to-r from-indigo-500 via-purple-500 to-pink-500 text-white py-12">
            <div className="mx-auto max-w-4xl px-6">
              <h1 className="text-3xl sm:text-4xl font-semibold tracking-tight">
                Terms of Service
              </h1>
              <p className="mt-2 opacity-90">Effective Date: {effectiveDate}</p>
              <p className="mt-1 opacity-90">Applies to {domain}</p>
            </div>
          </header>

          <div className="mx-auto max-w-4xl px-6 py-10">
            <nav className="mb-8">
              <ul className="flex flex-wrap gap-2 text-sm">
                {sections.map((s) => (
                  <li key={s.id}>
                    <a
                      className="inline-block rounded-full border border-gray-200 px-3 py-1 hover:bg-gray-100"
                      href={`#${s.id}`}
                    >
                      {s.title}
                    </a>
                  </li>
                ))}
              </ul>
            </nav>

            <article className="space-y-10">
              {sections.map(({ id, title, content, bullets }) => (
                <section key={id} id={id} className="scroll-mt-24">
                  <h2 className="text-2xl font-semibold mb-4">{title}</h2>
                  {content}
                  {bullets && (
                    <ul className="list-disc pl-6 space-y-2">
                      {bullets.map((b, i) => (
                        <li key={i}>{b}</li>
                      ))}
                    </ul>
                  )}
                </section>
              ))}
            </article>

            <footer className="mt-16 border-t pt-6 text-sm text-gray-600">
              <p>
                © {new Date().getFullYear()} {companyName}. All rights reserved.
                This page is provided for informational purposes and does not
                constitute legal advice.
              </p>
            </footer>
          </div>
        </main>
      </div>
      <Footer />
    </PageWrapper>
  );
}
